'use client';

import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  ComponentProps,
  FC,
  PropsWithChildren,
  ReactNode,
} from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { Button, ButtonGroup } from '../../../core-components';
import { EventClickArea, EventClickElement } from '@tuic/tracking';
import { twJoin } from 'tailwind-merge';

type ActionBase = {
  /**
   * The title of the action -- this is displayed as a Button's label.
   */
  title: string;

  /**
   * Indicates whether the action is of destructive kind. Destructive actions render a emphasized button.
   */
  primary?: boolean;
};

type ButtonAction = ActionBase & {
  as?: 'button';
} & ButtonHTMLAttributes<HTMLButtonElement>;
type AnchorAction = ActionBase & {
  as: 'a';
} & AnchorHTMLAttributes<HTMLAnchorElement>;

type ButtonOrAnchorAction = ButtonAction | AnchorAction;

type ActionCollection =
  | []
  | [ButtonOrAnchorAction]
  | [ButtonAction, ButtonOrAnchorAction]
  | [AnchorAction, ButtonOrAnchorAction];

type Props = PropsWithChildren<{
  /**
   * A boolean that determines whether the modal is open or closed. Default is `false` (closed).
   */
  open?: boolean;

  /**
   * Callback fired when the modal's open state changes.
   */
  onOpenChange?: ComponentProps<
    typeof AlertDialogPrimitive.Root
  >['onOpenChange'];

  /**
   * The title displayed at the top of the modal.
   */
  title?: ReactNode;

  /**
   * Defines the actions that will be displayed at the bottom of the modal. They can be either a button or an anchor action and you can provide up to two actions.
   */
  actions?: ActionCollection;

  /**
   * Specifies the container in which the modal will render. If not provided, the modal will render as a child of its parent in the DOM tree.
   */
  container?: ComponentProps<typeof AlertDialogPrimitive.Portal>['container'];

  /**
   * Make the title and Action-Items centered. If not provided, the title will be left-aligned and the Action-Items will be right aligned.
   */
  centerItems?: boolean;

  /**
   * Styles to be applied to the content div
   */
  containerClassName?: string;
}>;

/**
 * Renders a single action, either a button or anchor. If the action is destructive, it wraps the button within `AlertDialog.Action`, otherwise within `AlertDialog.Cancel`.
 */
const renderAction = (idx: number, action: ButtonAction | AnchorAction) => {
  const { title, primary, ...attrs } = action;

  // const buttonInner = attrs.as === 'a' ? <a {...attrs}>{title}</a> :

  const button = (
    <Button
      data-track-click-label={title}
      data-track-click-element={EventClickElement.Button}
      className="md:min-w-[10rem]"
      variant={primary ? 'primary' : 'secondary'}
      asChild={attrs.as === 'a'}
      {...(attrs.as !== 'a' ? attrs : {})}
    >
      {attrs.as === 'a' ? <a {...attrs}>{title}</a> : title}
    </Button>
  );

  if (primary) {
    return (
      <AlertDialogPrimitive.Action key={idx} asChild>
        {button}
      </AlertDialogPrimitive.Action>
    );
  }

  return (
    <AlertDialogPrimitive.Cancel key={idx} asChild>
      {button}
    </AlertDialogPrimitive.Cancel>
  );
};

/**
 * Renders the collection of actions. Returns `null` if the collection is empty or not provided.
 */
const renderActionCollection = (
  collection?: ActionCollection,
  centerActionCollection?: boolean,
) => {
  if (!collection || collection.length === 0) {
    return null;
  }

  return (
    <ButtonGroup
      orientation={centerActionCollection ? 'horizontal-center' : 'horizontal'}
    >
      {collection.map((action, idx) => renderAction(idx, action))}
    </ButtonGroup>
  );
};

/**
 * Renders the main content of the modal, which includes the title and description. Returns `null` if both title and children are not provided.
 */
const renderContent = (
  title: ReactNode,
  children: ReactNode,
  centerTitle: boolean,
) => {
  if (!title && !children) {
    return null;
  }

  return (
    <div className="space-y-4">
      {title && (
        <AlertDialogPrimitive.Title
          className={twJoin('headline-md', centerTitle && 'text-center')}
        >
          {title}
        </AlertDialogPrimitive.Title>
      )}
      {children && (
        <AlertDialogPrimitive.Description className="text-lg">
          {children}
        </AlertDialogPrimitive.Description>
      )}
    </div>
  );
};

// /**
//  * Renders the content either within a specified portal container or as a direct child if no container is provided.
//  */
// const renderOptionalContainer = (
//   container: Props['container'],
//   content: ReactNode,
// ) => {
//   if (!container) {
//     return content;
//   }
//
//   return (
//     <AlertDialogPrimitive.Portal container={container}>
//       {content}
//     </AlertDialogPrimitive.Portal>
//   );
// };

/**
 *
 * `AlertDialog` is a flexible component for displaying alert modals. It can render as a standalone modal or within a specified portal container and it supports both button and anchor actions. The modal uses [`@radix-ui/react-alert-dialog`](https://www.radix-ui.com/primitives/docs/components/alert-dialog#api-reference) as a foundation, so it provides accessibility and behavior out of the box.
 *
 * ## Usage
 *
 * To use the `AlertDialog`, simply import it and pass in the desired props. You can control the modal by providing the `open` property in conjunction with an `onOpenChange` handlers.
 *
 * ```tsx
 * import { useState } from 'react';
 * import { AlertDialog } from '@tui-cruises/mein-schiff-web-react-component-library';
 *
 * const [open, setOpen] = useState(false);
 *
 * // ...
 *
 * <AlertDialog
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="Are you sure?"
 *   actions={[
 *     { title: "Cancel" },
 *     { title: "Delete", destructive: true, onClick: handleDelete }
 *   ]}
 * >
 *   Do you really want to delete this item?
 * </AlertDialog>
 * ```
 *
 * ## Anatomy of an Action
 *
 *
 * In the context of the `AlertDialog`, an action is a crucial element that dictates how users can interact with the modal — be it through buttons or anchor links. Each action you provide must adhere to specific structures: `ButtonAction` or `AnchorAction`, with both sharing a set of common properties and some unique attributes. Here we delve into the anatomy of an action:
 *
 * ### Common Properties
 *
 * - **`title`** (Required): A string specifying the visible label of the action, representing the action text displayed on the button or link.
 * - **`destructive`** (Optional): A boolean indicating whether the action is potentially hazardous, affecting the action's styling to caution users.
 * - **`as`** (Optional for `ButtonAction`, Required for `AnchorAction`): A property to determine the type of action element — `'button'` for button actions and `'a'` for anchor actions.
 *
 * ### Unique Properties
 *
 * - **ButtonAction**: Incorporate any valid HTML button attributes, facilitating functionalities such as disabling the button (`disabled`) or adding a click handler (`onClick`).
 * - **AnchorAction**: Leverage any valid HTML anchor attributes to define the hyperlink specifics, such as the URL to navigate to (`href`) or defining the link’s opening location (`target`).
 *
 * ### Action Examples
 *
 * A button action with a click handler might look like this:
 *
 * ```tsx
 * { title: "Save", as: 'button', onClick: handleSave }
 * ```
 *
 * An anchor action directing to an external page could be structured as:
 *
 * ```tsx
 * { title: "Learn More", as: 'a', href: "#", target: "_blank" }
 * ```
 *
 * Understanding and correctly employing the action's anatomy aids in creating a rich, user-friendly modal experience.
 */
export const AlertDialog: FC<Props> = ({
  open,
  onOpenChange,
  children,
  title,
  actions,
  container,
  centerItems = true,
  containerClassName,
}) => {
  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <AlertDialogPrimitive.Portal container={container || undefined}>
        <AlertDialogPrimitive.Overlay
          className={twJoin(
            'fixed inset-0 z-dialog flex flex-col items-center justify-center bg-overlay backdrop-blur-sm',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
          )}
        >
          <AlertDialogPrimitive.Content
            data-track-click-area={EventClickArea.Dialog}
            data-track-dialog={`alert-dialog-${title}`}
            className={twJoin(
              'mx-4 flex max-w-[340px] flex-col flex-nowrap rounded-sm bg-surface-white p-8 text-center text-marine-high-emphasis sm:left-1/2 sm:w-[340px] sm:max-w-full md:w-[640px] lg:max-w-screen-sm',
              'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
              'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
              containerClassName,
            )}
          >
            <button className="h-0 w-0" name="trap-focus-placeholder" />
            <div className="space-y-8">
              {renderContent(title, children, centerItems)}
              {renderActionCollection(actions, centerItems)}
            </div>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Overlay>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
};
